/**
 * Base Service Utility
 * 
 * Provides common patterns and utilities for all service classes
 * to reduce duplication and improve consistency.
 * Enhanced with React 19 concurrent features and performance optimizations.
 */

import { sessionManager } from './sessionManager';
import { isSampleDataMode } from '@/lib/config/env';
import type { AuthRequestConfig } from './sessionManager';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface ServiceConfig {
  baseUrl?: string;
  timeout?: number;
  retries?: number;
  cacheTTL?: number; // Cache TTL in milliseconds
  enablePerformanceTracking?: boolean;
  maxConcurrentRequests?: number;
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  instanceId?: string;
}

export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
  performanceMetrics?: {
    duration: number;
    cacheHit: boolean;
    instanceId: string;
  };
}

export interface RequestQueueItem {
  id: string;
  priority: number;
  request: () => Promise<ServiceResponse<unknown>>;
  resolve: (value: ServiceResponse<unknown>) => void;
  reject: (error: Error) => void;
}

// =============================================================================
// BASE SERVICE CLASS
// =============================================================================

export abstract class BaseService {
  protected config: Required<ServiceConfig>;
  private cache = new Map<string, CacheEntry<unknown>>();
  private requestQueue: RequestQueueItem[] = [];
  private activeRequests = 0;
  private instanceId: string;
  private performanceMetrics = new Map<string, number[]>();
  
  private readonly DEFAULT_CONFIG: Required<ServiceConfig> = {
    baseUrl: '/api',
    timeout: 10000,
    retries: 3,
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    enablePerformanceTracking: true,
    maxConcurrentRequests: 6,
  };

  constructor(config: Partial<ServiceConfig> = {}) {
    this.config = { ...this.DEFAULT_CONFIG, ...config };
    this.instanceId = `service-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // =============================================================================
  // CONCURRENT REQUEST MANAGEMENT
  // =============================================================================

  /**
   * Queue request with priority handling for React 19 concurrent features
   */
  private async queueRequest<T>(
    request: () => Promise<ServiceResponse<T>>,
    priority: number = 0
  ): Promise<ServiceResponse<T>> {
    if (this.activeRequests < this.config.maxConcurrentRequests) {
      this.activeRequests++;
      try {
        return await request();
      } finally {
        this.activeRequests--;
        this.processQueue();
      }
    }

    return new Promise((resolve, reject) => {
      const queueItem: RequestQueueItem = {
        id: `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        priority,
        request: request as () => Promise<ServiceResponse<unknown>>,
        resolve: resolve as (value: ServiceResponse<unknown>) => void,
        reject,
      };

      // Insert based on priority (higher priority first)
      const insertIndex = this.requestQueue.findIndex(item => item.priority < priority);
      if (insertIndex === -1) {
        this.requestQueue.push(queueItem);
      } else {
        this.requestQueue.splice(insertIndex, 0, queueItem);
      }
    });
  }

  /**
   * Process queued requests
   */
  private async processQueue(): Promise<void> {
    if (this.requestQueue.length === 0 || this.activeRequests >= this.config.maxConcurrentRequests) {
      return;
    }

    const queueItem = this.requestQueue.shift();
    if (!queueItem) return;

    this.activeRequests++;
    try {
      const result = await queueItem.request();
      queueItem.resolve(result);
    } catch (error) {
      queueItem.reject(error as Error);
    } finally {
      this.activeRequests--;
      this.processQueue();
    }
  }

  // =============================================================================
  // PERFORMANCE TRACKING
  // =============================================================================

  /**
   * Track performance metrics for React 19 optimization
   */
  private trackPerformance(operation: string, duration: number): void {
    if (!this.config.enablePerformanceTracking) return;

    if (!this.performanceMetrics.has(operation)) {
      this.performanceMetrics.set(operation, []);
    }

    const metrics = this.performanceMetrics.get(operation)!;
    metrics.push(duration);

    // Keep only last 100 measurements
    if (metrics.length > 100) {
      metrics.shift();
    }

    // Log slow operations
    if (duration > 1000) {
      console.warn(`[${this.instanceId}] Slow operation detected: ${operation} took ${duration}ms`);
    }
  }

  /**
   * Get performance metrics for an operation
   */
  protected getPerformanceMetrics(operation: string): {
    average: number;
    min: number;
    max: number;
    count: number;
  } | null {
    const metrics = this.performanceMetrics.get(operation);
    if (!metrics || metrics.length === 0) return null;

    return {
      average: metrics.reduce((sum, val) => sum + val, 0) / metrics.length,
      min: Math.min(...metrics),
      max: Math.max(...metrics),
      count: metrics.length,
    };
  }

  // =============================================================================
  // HTTP REQUEST METHODS
  // =============================================================================

  /**
   * Make authenticated GET request with concurrent handling
   */
  protected async get<T>(
    endpoint: string,
    options: AuthRequestConfig & { priority?: number } = {}
  ): Promise<ServiceResponse<T>> {
    const { priority = 0, ...requestOptions } = options;
    return this.queueRequest(
      () => this.request<T>('GET', endpoint, undefined, { ...requestOptions, requestPriority: 'normal' }),
      priority
    );
  }

  /**
   * Make authenticated POST request with concurrent handling
   */
  protected async post<T>(
    endpoint: string,
    data?: unknown,
    options: AuthRequestConfig & { priority?: number } = {}
  ): Promise<ServiceResponse<T>> {
    const { priority = 1, ...requestOptions } = options; // Higher priority for mutations
    return this.queueRequest(
      () => this.request<T>('POST', endpoint, data, { ...requestOptions, requestPriority: 'high' }),
      priority
    );
  }

  /**
   * Make authenticated PUT request with concurrent handling
   */
  protected async put<T>(
    endpoint: string,
    data?: unknown,
    options: AuthRequestConfig & { priority?: number } = {}
  ): Promise<ServiceResponse<T>> {
    const { priority = 1, ...requestOptions } = options; // Higher priority for mutations
    return this.queueRequest(
      () => this.request<T>('PUT', endpoint, data, { ...requestOptions, requestPriority: 'high' }),
      priority
    );
  }

  /**
   * Make authenticated DELETE request with concurrent handling
   */
  protected async delete<T>(
    endpoint: string,
    options: AuthRequestConfig & { priority?: number } = {}
  ): Promise<ServiceResponse<T>> {
    const { priority = 1, ...requestOptions } = options; // Higher priority for mutations
    return this.queueRequest(
      () => this.request<T>('DELETE', endpoint, undefined, { ...requestOptions, requestPriority: 'high' }),
      priority
    );
  }

  /**
   * Core request method with error handling, retries, and performance tracking
   */
  private async request<T>(
    method: string,
    endpoint: string,
    data?: unknown,
    options: AuthRequestConfig = {}
  ): Promise<ServiceResponse<T>> {
    const startTime = performance.now();
    const url = `${this.config.baseUrl}${endpoint}`;
    const operationId = `${method} ${endpoint}`;
    
    try {
      const response = await sessionManager.authenticatedFetch(url, {
        method,
        body: data ? JSON.stringify(data) : undefined,
        timeout: this.config.timeout,
        retryCount: 0,
        ...options,
      });

      const responseData = await response.json();
      const duration = performance.now() - startTime;
      
      this.trackPerformance(operationId, duration);

      return {
        success: true,
        data: responseData as T,
        timestamp: Date.now(),
        performanceMetrics: {
          duration,
          cacheHit: false,
          instanceId: this.instanceId,
        },
      };

    } catch (error) {
      const duration = performance.now() - startTime;
      this.trackPerformance(`${operationId}-error`, duration);
      
      console.error(`[${this.instanceId}] Service request failed: ${method} ${endpoint}`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: Date.now(),
        performanceMetrics: {
          duration,
          cacheHit: false,
          instanceId: this.instanceId,
        },
      };
    }
  }

  // =============================================================================
  // ENHANCED CACHING UTILITIES
  // =============================================================================

  /**
   * Get data from cache or fetch if not available/expired with performance tracking
   */
  protected async getCached<T>(
    key: string,
    fetcher: () => Promise<ServiceResponse<T>>,
    ttl?: number
  ): Promise<ServiceResponse<T>> {
    const startTime = performance.now();
    const cached = this.getFromCache<T>(key);
    
    if (cached) {
      const duration = performance.now() - startTime;
      this.trackPerformance(`cache-hit-${key}`, duration);
      
      return {
        success: true,
        data: cached,
        timestamp: Date.now(),
        performanceMetrics: {
          duration,
          cacheHit: true,
          instanceId: this.instanceId,
        },
      };
    }

    const result = await fetcher();
    const duration = performance.now() - startTime;
    this.trackPerformance(`cache-miss-${key}`, duration);
    
    if (result.success && result.data) {
      this.setCache(key, result.data, ttl);
    }

    return {
      ...result,
      performanceMetrics: {
        duration,
        cacheHit: false,
        instanceId: this.instanceId,
      },
    };
  }

  /**
   * Get item from cache with instance tracking
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    
    if (!entry) return null;
    
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set item in cache with instance tracking
   */
  private setCache<T>(key: string, data: T, ttl?: number): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.cacheTTL,
      instanceId: this.instanceId,
    };
    
    this.cache.set(key, entry);
  }

  /**
   * Clear cache item or all cache with performance tracking
   */
  protected clearCache(key?: string): void {
    const startTime = performance.now();
    
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
    
    const duration = performance.now() - startTime;
    this.trackPerformance('cache-clear', duration);
  }

  // =============================================================================
  // VALIDATION UTILITIES
  // =============================================================================

  /**
   * Validate required fields
   */
  protected validateRequired(
    data: Record<string, unknown>,
    fields: string[]
  ): { valid: boolean; missing: string[] } {
    const missing = fields.filter(field => 
      data[field] === undefined || 
      data[field] === null || 
      data[field] === ''
    );

    return {
      valid: missing.length === 0,
      missing,
    };
  }

  /**
   * Validate email format using simple regex
   */
  protected validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate ID format (10-digit numeric)
   */
  protected validateId(id: string): boolean {
    return /^\d{10}$/.test(id);
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Add delay (useful for sample data mode)
   */
  protected async delay(ms: number = 100): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Simulate sample data behavior
   */
  protected async withSampleDelay<T>(operation: () => Promise<T>): Promise<T> {
    if (isSampleDataMode()) {
      await this.delay();
    }
    return operation();
  }

  /**
   * Handle errors consistently
   */
  protected handleError(error: unknown, context: string): ServiceResponse<never> {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`${context}:`, error);
    
    return {
      success: false,
      error: message,
      timestamp: Date.now(),
    };
  }

  /**
   * Build query string from parameters
   */
  protected buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Filter array by search term
   */
  protected filterBySearch<T>(
    items: T[],
    searchTerm: string,
    searchFields: (keyof T)[]
  ): T[] {
    if (!searchTerm.trim()) return items;

    const term = searchTerm.toLowerCase();
    
    return items.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        return value && String(value).toLowerCase().includes(term);
      })
    );
  }

  /**
   * Sort array by field
   */
  protected sortBy<T>(
    items: T[],
    field: keyof T,
    direction: 'asc' | 'desc' = 'asc'
  ): T[] {
    return [...items].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Paginate array
   */
  protected paginate<T>(
    items: T[],
    page: number = 1,
    pageSize: number = 10
  ): {
    items: T[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  } {
    const total = items.length;
    const totalPages = Math.ceil(total / pageSize);
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return {
      items: items.slice(start, end),
      pagination: {
        page,
        pageSize,
        total,
        totalPages,
      },
    };
  }

  // =============================================================================
  // BATCH OPERATIONS FOR REACT 19 CONCURRENT FEATURES
  // =============================================================================

  /**
   * Execute multiple operations concurrently with priority handling
   */
  protected async batchOperations<T>(
    operations: Array<{
      operation: () => Promise<ServiceResponse<T>>;
      priority?: number;
      id?: string;
    }>
  ): Promise<ServiceResponse<T>[]> {
    const startTime = performance.now();
    
    try {
      const promises = operations.map(({ operation, priority = 0 }) =>
        this.queueRequest(operation, priority)
      );

      const results = await Promise.allSettled(promises);
      const duration = performance.now() - startTime;
      
      this.trackPerformance('batch-operations', duration);

      return results.map(result => 
        result.status === 'fulfilled' 
          ? result.value 
          : {
              success: false,
              error: result.reason?.message || 'Batch operation failed',
              timestamp: Date.now(),
              performanceMetrics: {
                duration,
                cacheHit: false,
                instanceId: this.instanceId,
              },
            }
      );
    } catch (error) {
      const duration = performance.now() - startTime;
      this.trackPerformance('batch-operations-error', duration);
      
      console.error(`[${this.instanceId}] Batch operations failed:`, error);
      return [{
        success: false,
        error: error instanceof Error ? error.message : 'Batch operations failed',
        timestamp: Date.now(),
        performanceMetrics: {
          duration,
          cacheHit: false,
          instanceId: this.instanceId,
        },
      }];
    }
  }

  /**
   * Get service health metrics for React 19 performance monitoring
   */
  protected getServiceHealth(): {
    instanceId: string;
    activeRequests: number;
    queuedRequests: number;
    cacheSize: number;
    performanceMetrics: Record<string, {
      average: number;
      min: number;
      max: number;
      count: number;
    }>;
  } {
    const performanceMetrics: Record<string, {
      average: number;
      min: number;
      max: number;
      count: number;
    }> = {};

    for (const [operation, metrics] of this.performanceMetrics.entries()) {
      if (metrics.length > 0) {
        performanceMetrics[operation] = {
          average: metrics.reduce((sum, val) => sum + val, 0) / metrics.length,
          min: Math.min(...metrics),
          max: Math.max(...metrics),
          count: metrics.length,
        };
      }
    }

    return {
      instanceId: this.instanceId,
      activeRequests: this.activeRequests,
      queuedRequests: this.requestQueue.length,
      cacheSize: this.cache.size,
      performanceMetrics,
    };
  }

  // =============================================================================
  // LIFECYCLE METHODS
  // =============================================================================

  /**
   * Enhanced cleanup method for React 19 service disposal
   */
  dispose(): void {
    const startTime = performance.now();
    
    // Cancel all queued requests
    this.requestQueue.forEach(queueItem => {
      queueItem.reject(new Error('Service disposed'));
    });
    this.requestQueue.length = 0;
    
    // Clear all caches
    this.clearCache();
    
    // Clear performance metrics
    this.performanceMetrics.clear();
    
    // Reset counters
    this.activeRequests = 0;
    
    const duration = performance.now() - startTime;
    
    // Only log disposal if there was significant cleanup work or if it took a long time
    if (duration > 10 || this.cache.size > 0 || this.requestQueue.length > 0) {
      console.log(`[${this.instanceId}] Service disposed in ${duration}ms`);
    }
  }

  /**
   * Wait for all active requests to complete (useful for graceful shutdown)
   */
  protected async waitForCompletion(timeout: number = 5000): Promise<boolean> {
    const startTime = Date.now();
    
    while (this.activeRequests > 0 || this.requestQueue.length > 0) {
      if (Date.now() - startTime > timeout) {
        console.warn(`[${this.instanceId}] Timeout waiting for requests to complete`);
        return false;
      }
      
      await this.delay(50);
    }
    
    return true;
  }
}

// =============================================================================
// SERVICE FACTORY
// =============================================================================

/**
 * Create service instance with common configuration
 */
export function createService<T extends BaseService>(
  ServiceClass: new (config?: Partial<ServiceConfig>) => T,
  config?: Partial<ServiceConfig>
): T {
  return new ServiceClass(config);
}

// =============================================================================
// COMMON SERVICE MIXINS
// =============================================================================

/**
 * Mixin for services that need CRUD operations
 */
export interface CrudOperations<T, CreateData, UpdateData> {
  getAll(): Promise<ServiceResponse<T[]>>;
  getById(id: string): Promise<ServiceResponse<T>>;
  create(data: CreateData): Promise<ServiceResponse<T>>;
  update(id: string, data: UpdateData): Promise<ServiceResponse<T>>;
  delete(id: string): Promise<ServiceResponse<boolean>>;
}

/**
 * Mixin for services that need search and filtering
 */
export interface SearchOperations<T> {
  search(query: string): Promise<ServiceResponse<T[]>>;
  filter(criteria: Record<string, unknown>): Promise<ServiceResponse<T[]>>;
}

/**
 * Mixin for services that need statistics
 */
export interface StatsOperations<T> {
  getStats(): Promise<ServiceResponse<T>>;
} 
